import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { API_URL } from "../config/api";
import { FAIRY_KEYS, getKeyById, MIN_KEY_LEVEL } from "../constants/keys";
import { GAME_QUESTION_COUNT } from "../constants/gameLevels";
import {
  readKeyUsagePreference,
  writeKeyUsagePreference,
} from "../utils/keyUsagePreference";
import "./VocabTooltip.css";
import "./GameDifficultySelect.css";
import "./KeyUsageScreen.css";

const DEFAULT_OPTION = {
  value: "none",
  label: "Ne pas utiliser de parchemin pour le prochain jeu",
};

function isKeyEligible(keyId, keyEligibility) {
  return keyEligibility?.[keyId]?.eligible === true;
}

function buildDropdownOptions(keyCounts, keyEligibility) {
  const ownedOptions = FAIRY_KEYS.filter(
    (key) => (keyCounts[key.id] || 0) > 0 && isKeyEligible(key.id, keyEligibility)
  ).map((key) => ({
    value: key.id,
    label: `Utiliser le parchemin ${key.name} pour le prochain jeu`,
  }));

  return [DEFAULT_OPTION, ...ownedOptions];
}

function resolveSelectedOption(selectedValue, keyCounts, keyEligibility) {
  if (selectedValue === "none") return DEFAULT_OPTION;

  const fromOptions = buildDropdownOptions(keyCounts, keyEligibility).find(
    (option) => option.value === selectedValue
  );
  if (fromOptions) return fromOptions;

  const key = getKeyById(selectedValue);
  if (key) {
    return {
      value: selectedValue,
      label: `Utiliser le parchemin ${key.name} pour le prochain jeu`,
    };
  }

  return DEFAULT_OPTION;
}

function tooltipText(keyItem) {
  return keyItem.description.replace(/^Parchemin\.\s*/i, "");
}

export default function KeyUsageScreen() {
  const [keyCounts, setKeyCounts] = useState({});
  const [keyEligibility, setKeyEligibility] = useState({});
  const [currentLevel, setCurrentLevel] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedValue, setSelectedValue] = useState(() => {
    const stored = readKeyUsagePreference();
    return stored ?? "none";
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const totalKeys = useMemo(
    () => FAIRY_KEYS.reduce((sum, key) => sum + (keyCounts[key.id] || 0), 0),
    [keyCounts]
  );

  const eligibleKeyCount = useMemo(
    () =>
      FAIRY_KEYS.filter(
        (key) => (keyCounts[key.id] || 0) > 0 && isKeyEligible(key.id, keyEligibility)
      ).length,
    [keyCounts, keyEligibility]
  );

  const options = useMemo(
    () => buildDropdownOptions(keyCounts, keyEligibility),
    [keyCounts, keyEligibility]
  );

  const selectedOption = useMemo(
    () => resolveSelectedOption(selectedValue, keyCounts, keyEligibility),
    [selectedValue, keyCounts, keyEligibility]
  );

  const syncSelection = useCallback((counts, eligibility, stored = readKeyUsagePreference()) => {
    if (stored && (counts[stored] || 0) > 0 && isKeyEligible(stored, eligibility)) {
      setSelectedValue(stored);
      return;
    }

    if (stored) {
      setSelectedValue("none");
      writeKeyUsagePreference(null);
    }
  }, []);

  const loadKeyData = useCallback(async () => {
    setLoading(true);
    try {
      const [countsRes, masteryRes] = await Promise.all([
        fetch(`${API_URL}/keys/counts`),
        fetch(`${API_URL}/mastery/progress`),
      ]);

      let counts = {};
      if (countsRes.ok) {
        const json = await countsRes.json();
        counts = json.counts || {};
        setKeyCounts(counts);
      }

      let level = 1;
      if (masteryRes.ok) {
        const masteryJson = await masteryRes.json();
        level = masteryJson.maxUnlockedLevel || 1;
        setCurrentLevel(level);
      }

      let eligibility = {};
      if (level >= MIN_KEY_LEVEL) {
        const eligibilityRes = await fetch(
          `${API_URL}/keys/eligibility?level=${level}&count=${GAME_QUESTION_COUNT}`
        );
        if (eligibilityRes.ok) {
          const eligibilityJson = await eligibilityRes.json();
          eligibility = eligibilityJson.keys || {};
          setKeyEligibility(eligibility);
          if (eligibilityJson.currentLevel) {
            setCurrentLevel(eligibilityJson.currentLevel);
          }
        }
      } else {
        setKeyEligibility({});
      }

      syncSelection(counts, eligibility);
    } catch {
      // ignore network errors — UI stays usable with empty counts
    } finally {
      setLoading(false);
    }
  }, [syncSelection]);

  useEffect(() => {
    loadKeyData();
  }, [loadKeyData]);

  useEffect(() => {
    if (loading) return;

    if (selectedValue === "none") return;

    const owned = (keyCounts[selectedValue] || 0) > 0;
    const eligible = isKeyEligible(selectedValue, keyEligibility);

    if (!owned || !eligible) {
      setSelectedValue("none");
      writeKeyUsagePreference(null);
    }
  }, [loading, keyCounts, keyEligibility, selectedValue]);

  useEffect(() => {
    if (!dropdownOpen) return undefined;

    const handlePointerDown = (event) => {
      if (dropdownRef.current?.contains(event.target)) return;
      setDropdownOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setDropdownOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [dropdownOpen]);

  const handleSelect = (value) => {
    setSelectedValue(value);
    writeKeyUsagePreference(value === "none" ? null : value);
    setDropdownOpen(false);
  };

  const dropdownDisabled = loading || totalKeys === 0 || eligibleKeyCount === 0;

  return (
    <div className="key-usage-screen">
      <header className="key-usage-screen__header">
        <h2 className="game-difficulty-title">Utilisation d&apos;un parchemin</h2>
      </header>

      <div className="key-usage-screen__body">
        <p className="key-usage-screen__intro">
          Si vous avez un parchemin, vous pouvez choisir de l&apos;utiliser pour le prochain
          jeu. Les parchemins sont une aide précieuse pour avancer rapidement dans un niveau.
          Assurez-vous toutefois de maîtriser le vocabulaire avant d&apos;utiliser un
          parchemin pour en tirer tous les bénéfices. Les potions sont désactivées lorsqu&apos;un
          parchemin est utilisé.
        </p>

        <p className="key-usage-screen__note">
          <span className="key-usage-screen__note-label">À savoir&nbsp;:</span> si vous
          quittez le jeu avant son terme le parchemin sera considéré comme perdu. Ayez donc
          dans l&apos;intention d&apos;aller au bout du jeu pour ne pas perdre le parchemin.
        </p>

        <div className="key-usage-screen__selector">
          <div
            className={`key-usage-dropdown${
              dropdownOpen ? " key-usage-dropdown--open" : ""
            }${dropdownDisabled ? " key-usage-dropdown--disabled" : ""}`}
            ref={dropdownRef}
          >
            <button
              type="button"
              className="key-usage-dropdown__trigger"
              disabled={dropdownDisabled}
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
              onClick={() => {
                if (dropdownDisabled) return;
                setDropdownOpen((open) => !open);
              }}
            >
              <span className="key-usage-dropdown__label">{selectedOption.label}</span>
              <ChevronDown
                size={16}
                strokeWidth={2}
                className="key-usage-dropdown__chevron"
                aria-hidden="true"
              />
            </button>

            {dropdownOpen && !dropdownDisabled && (
              <ul className="key-usage-dropdown__menu" role="listbox">
                {options.map((option) => (
                  <li key={option.value} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={option.value === selectedValue}
                      className={`key-usage-dropdown__option${
                        option.value === selectedValue
                          ? " key-usage-dropdown__option--selected"
                          : ""
                      }`}
                      onClick={() => handleSelect(option.value)}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {!loading && totalKeys === 0 && (
            <p className="key-usage-screen__empty">Vous ne possédez aucun parchemin.</p>
          )}

          {!loading && totalKeys > 0 && eligibleKeyCount === 0 && (
            <p className="key-usage-screen__empty">
              {currentLevel < MIN_KEY_LEVEL
                ? "Les parchemins ne sont utilisables qu'à partir du niveau 2."
                : "Aucun parchemin ne peut être activé pour le niveau en cours (candidats insuffisants)."}
            </p>
          )}
        </div>

        <div className="key-usage-inventory" aria-label="Vos parchemins">
          {FAIRY_KEYS.map((keyItem, index) => {
            const quantity = keyCounts[keyItem.id] || 0;
            const isEmpty = quantity === 0;
            const tooltipPlacement =
              index === 0
                ? " key-usage-inventory__tooltip--start"
                : index === FAIRY_KEYS.length - 1
                  ? " key-usage-inventory__tooltip--end"
                  : "";

            return (
              <article
                key={keyItem.id}
                className={`key-usage-inventory__item key-usage-inventory__item--${keyItem.id}${
                  isEmpty ? " key-usage-inventory__item--empty" : ""
                }`}
              >
                <div className="key-usage-inventory__visual">
                  <img
                    src={keyItem.image}
                    alt=""
                    className="key-usage-inventory__image"
                    decoding="sync"
                    draggable={false}
                    aria-hidden="true"
                  />
                </div>

                <span className="key-usage-inventory__quantity" aria-label={`Quantité : ${quantity}`}>
                  {quantity}
                </span>

                <span className="vocab-tooltip-wrap key-usage-inventory__name-wrap">
                  <span
                    className="key-usage-inventory__name"
                    tabIndex={0}
                    aria-describedby={`key-usage-tooltip-${keyItem.id}`}
                  >
                    {keyItem.name}
                  </span>
                  <span
                    id={`key-usage-tooltip-${keyItem.id}`}
                    className={`vocab-tooltip vocab-tooltip--below key-usage-inventory__tooltip${tooltipPlacement}`}
                    role="tooltip"
                  >
                    {tooltipText(keyItem)}
                  </span>
                </span>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
